<script>
import { GlLoadingIcon } from '@gitlab/ui';
import * as Sentry from '@sentry/browser';
import ErrorAlert from '~/javascripts/components/error_alert.vue';
import getCurrentCustomerAttributesQuery from '~/javascripts/billing_account/graphql/queries/get_current_customer_attributes.query.graphql';
import CompanyInformation from '~/javascripts/billing_account/components/company_information.vue';
import { BILLING_ACCOUNT_ERROR_DICTIONARY } from '~/javascripts/billing_account/error_dictionary';

const INFO_LINE_CLASSES = 'gl-leading-20';

export default {
  name: 'CustomerFields',
  components: {
    GlLoadingIcon,
    ErrorAlert,
    CompanyInformation,
  },
  data() {
    return {
      customer: {
        email: '',
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        companyName: '',
        // Should be refactored to using `tax`-names instead of `vat`:
        // https://gitlab.com/gitlab-org/customers-gitlab-com/-/issues/7469
        vatFieldVisible: false,
        vatCode: '',
      },
      error: null,
    };
  },
  computed: {
    fullName() {
      return [this.customer?.firstName, this.customer?.lastName].filter(Boolean).join(' ');
    },
    cityStatePostalode() {
      return [this.customer?.city, this.customer?.state, this.customer?.zipCode]
        .filter(Boolean)
        .join(' ');
    },
    showCompanyInfo() {
      return this.customer?.companyName || this.customer?.vatCode || this.customer?.vatFieldVisible;
    },
    isLoading() {
      return this.$apollo.queries.customer?.loading;
    },
  },
  methods: {
    clearError() {
      this.error = null;
    },
    handleError(error) {
      this.error = error;

      Sentry.captureException(error);
    },
  },
  apollo: {
    customer: {
      query: getCurrentCustomerAttributesQuery,
      update(data) {
        return data.currentCustomerAttributes;
      },
      error(error) {
        this.handleError(error);
      },
    },
  },
  INFO_LINE_CLASSES,
  BILLING_ACCOUNT_ERROR_DICTIONARY,
};
</script>

<template>
  <div data-testid="customer-information">
    <gl-loading-icon v-if="isLoading" class="gl-mx-auto" size="lg" />

    <error-alert
      v-else-if="error"
      :dismissible="true"
      :error-dictionary="$options.BILLING_ACCOUNT_ERROR_DICTIONARY"
      :error="error"
      @dismiss="clearError"
    />

    <div v-else>
      <h3 class="gl-mb-5">Customer information</h3>
      <section>
        <div class="gl-mb-5">
          <div :class="$options.INFO_LINE_CLASSES" data-testid="customer-fields-customer-name">
            {{ fullName }}
          </div>
          <div
            :class="[$options.INFO_LINE_CLASSES, 'gl-mb-5']"
            data-testid="customer-fields-customer-email"
          >
            {{ customer?.email }}
          </div>

          <div
            v-if="customer?.address1"
            :class="$options.INFO_LINE_CLASSES"
            data-testid="customer-fields-address-one"
          >
            {{ customer?.address1 }}
          </div>
          <div
            v-if="customer?.address2"
            :class="$options.INFO_LINE_CLASSES"
            data-testid="customer-fields-address-two"
          >
            {{ customer?.address2 }}
          </div>

          <div
            v-if="cityStatePostalode"
            :class="$options.INFO_LINE_CLASSES"
            data-testid="customer-fields-city-state-postal-code"
          >
            {{ cityStatePostalode }}
          </div>
          <div
            v-if="customer?.country"
            :class="$options.INFO_LINE_CLASSES"
            data-testid="customer-fields-country"
          >
            {{ customer?.country }}
          </div>
        </div>

        <company-information
          v-if="showCompanyInfo"
          :class="$options.INFO_LINE_CLASSES"
          :zuora-account-name="customer?.companyName"
          :vat-code="customer?.vatCode"
          :show-vat-code="customer?.vatFieldVisible"
        />
      </section>
    </div>
  </div>
</template>
